import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Trash2,
  Edit,
  Search,
  Table2,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Download,
} from "lucide-react";
import axios from "axios";
import * as XLSX from "xlsx";
import Apiurl from "../../environment/Apiurl";
// Import the sample XLSX file from assets
import sampleXlsx from "../../../assets/medicine list.xlsx";

function MedicineMaster() {
  const fileFormRef = useRef(null);
  const [responseData, setResponseData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [loadingPrivileges, setLoadingPrivileges] = useState(true);
  const [userPrivileges, setUserPrivileges] = useState({});
  const [addData, setAddData] = useState({
    medicine_name: "",
    medicine_type: "",
    hsn_code: "",
    category: "",
    manufacturer: "",
    notes: "",
  });
  const [updateData, setUpdateData] = useState({
    medicine_name: "",
    medicine_type: "",
    hsn_code: "",
    category: "",
    manufacturer: "",
    notes: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [showEntries, setShowEntries] = useState("10");

  // Fetch user privileges
  const fetchUserPrivileges = async () => {
    try {
      setLoadingPrivileges(true);
      const userRoleId = localStorage.getItem("role_id") || "unknown";
      console.log("Fetching privileges for role_id:", userRoleId);
      if (userRoleId === "unknown") {
        throw new Error(
          "No role assigned to the user. Please contact the administrator."
        );
      }
      const response = await axios.get(`${Apiurl}/roles_and_privillage`);
      console.log("Fetched roles data:", response.data);
      if (!response.data || !Array.isArray(response.data)) {
        throw new Error("Invalid roles data received");
      }
      const roleData = response.data.find(
        (role) => role.role_id === userRoleId
      );
      if (!roleData) {
        throw new Error(`No role data found for role_id: ${userRoleId}`);
      }
      const privileges = {};
      roleData.pages.forEach((page) => {
        privileges[page.page] = {
          access: page.access,
          ...page.priviallages,
        };
      });
      console.log("Set user privileges:", privileges);
      setUserPrivileges(privileges);
      setError(null);
    } catch (error) {
      console.error(
        "Error fetching user privileges:",
        error.message,
        error.response?.data
      );
      setError(
        error.message || "Failed to fetch user privileges. Please try again."
      );
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingPrivileges(false);
    }
  };

  // Check permission for Medicine Master page
  const hasPermission = (permission) => {
    const privileges = userPrivileges["Medicine List"];
    if (!privileges) {
      console.log("No privileges found for Medicine Master page");
      return false;
    }
    console.log(
      `Checking permission '${permission}' for Medicine Master:`,
      privileges
    );
    if (!privileges || !privileges.access) return false;
    if (permission === "access") return privileges.access;
    return privileges.all || privileges[permission] || false;
  };

  // Fetch medicine data
  const fetchData = async () => {
    if (!hasPermission("read")) {
      console.warn("Read permission denied for Medicine Master page");
      setError("You do not have permission to view medicine records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      console.log("Fetching medicine data from:", `${Apiurl}/medicine_master`);
      const response = await axios.get(`${Apiurl}/medicine_master`);
      console.log("Medicine data fetched:", response.data);
      if (!response.data || !Array.isArray(response.data)) {
        throw new Error("Invalid medicine data received");
      }
      setResponseData(response.data.reverse());
      setError(null);
    } catch (error) {
      console.error(
        "Error fetching medicine data:",
        error.message,
        error.response?.data
      );
      setError("Failed to fetch medicine data. Please try again.");
      setTimeout(() => setError(null), 3000);
      setResponseData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Handle file upload
  const handleFileUpload = async (e) => {
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Medicine Master page");
      setError("You do not have permission to add medicine records.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    const file = e.target.files[0];
    if (!file) {
      setError("No file selected.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (!file.name.match(/\.(csv|xlsx)$/)) {
      setError("Please upload a CSV or Excel file.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    setLoadingUpload(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // Validate headers
        const headers = jsonData[0].map((header) =>
          header.toLowerCase().trim()
        );
        const requiredFields = ["medicine_name", "medicine_type", "hsn_code"];
        const optionalFields = ["manufacturer", "notes", "category"];
        const missingFields = requiredFields.filter(
          (field) => !headers.includes(field)
        );
        if (missingFields.length > 0) {
          setError(`Missing required columns: ${missingFields.join(", ")}`);
          setLoadingUpload(false);
          setTimeout(() => setError(null), 3000);
          return;
        }

        // Parse rows
        const records = jsonData.slice(1).map((row) => {
          const rowData = {};
          headers.forEach((header, index) => {
            if (
              requiredFields.includes(header) ||
              optionalFields.includes(header)
            ) {
              rowData[header] = row[index] ? row[index].toString().trim() : "";
            }
          });
          return {
            ...rowData,
            created_by: localStorage.getItem("user_id") || "system",
          };
        });

        // Validate records
        const validRecords = records.filter((record) => {
          return (
            record.medicine_name && record.medicine_type && record.hsn_code
          );
        });

        if (validRecords.length === 0) {
          setError("No valid records found in the file.");
          setLoadingUpload(false);
          setTimeout(() => setError(null), 3000);
          return;
        }

        // Send to backend
        try {
          console.log("Uploading records:", validRecords);
          const response = await axios.post(`${Apiurl}/medicine_master/bulk`, {
            medicines: validRecords,
          });
          console.log("Bulk upload response:", response.data);
          setSuccessMessage(
            `Successfully uploaded ${validRecords.length} medicine records.`
          );
          fetchData();
          fileFormRef.current.reset();
          setTimeout(() => setSuccessMessage(null), 3000);
        } catch (error) {
          console.error(
            "Error uploading records:",
            error.message,
            error.response?.data
          );
          setError(
            error.response?.data?.message ||
              "Failed to upload medicine records."
          );
          setTimeout(() => setError(null), 3000);
        } finally {
          setLoadingUpload(false);
        }
      };

      reader.onerror = () => {
        setError("Error reading file. Please try again.");
        setLoadingUpload(false);
        setTimeout(() => setError(null), 3000);
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Error processing file:", error.message);
      setError("Failed to process file. Please try again.");
      setLoadingUpload(false);
      setTimeout(() => setError(null), 3000);
    }
  };

  // Handle sample file download
  const handleSampleDownload = () => {
    console.log("Initiating sample XLSX download");
    const link = document.createElement("a");
    link.href = sampleXlsx;
    link.download = "medicine list.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setSuccessMessage("Sample file downloaded successfully.");
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Initialize privileges and data
  useEffect(() => {
    console.log("Fetching initial privileges...");
    fetchUserPrivileges();
  }, []);

  useEffect(() => {
    if (!loadingPrivileges && hasPermission("access")) {
      console.log("Access granted for Medicine Master page");
      if (hasPermission("read")) {
        console.log("Fetching medicine data due to read permission");
        fetchData();
      } else {
        console.warn("Read permission denied for Medicine Master page");
        setError("You do not have permission to view medicine records.");
        setTimeout(() => setError(null), 3000);
      }
    } else if (!loadingPrivileges) {
      console.warn("Access permission denied for Medicine Master page");
      setError("You do not have permission to access this page.");
      setTimeout(() => setError(null), 3000);
    }
  }, [userPrivileges, loadingPrivileges]);

  // Filter data
  const filteredData = useMemo(() => {
    const result = responseData.filter((item) =>
      [
        item.medicine_name,
        item.manufacturer,
        item.medicine_type,
        item.category,
        item.hsn_code,
      ].some((prop) =>
        (prop || "").toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    console.log("Filtered medicine data:", result);
    return result;
  }, [responseData, searchTerm]);

  // Calculate items per page
  const itemsPerPage = useMemo(() => {
    const count =
      showEntries === "All" ? filteredData.length || 1 : Number(showEntries);
    console.log("Calculated items per page:", count);
    return count;
  }, [showEntries, filteredData]);

  // Reset active page on search or entries change
  useEffect(() => {
    console.log(
      "Resetting active page due to searchTerm or showEntries change"
    );
    setActivePage(1);
  }, [searchTerm, showEntries]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const indexOfLastItem = activePage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const startIndex = (activePage - 1) * itemsPerPage;

  // Generate page list
  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (activePage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (activePage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          activePage - 1,
          activePage,
          activePage + 1,
          "...",
          totalPages
        );
      }
    }
    console.log("Generated page list:", pages);
    return pages;
  };

  // Handle add input changes
  const handleAddInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Add form input changed: ${name} = ${value}`);
    setAddData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle page click for pagination
  const handlePageClick = (page) => {
    if (page !== "..." && page !== activePage) {
      console.log("Navigating to page:", page);
      setActivePage(page);
    }
  };

  // Handle add form submission
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Medicine Master page");
      setError("You do not have permission to add medicine records.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    setLoading(true);
    try {
      console.log("Submitting add medicine:", addData);
      await axios.post(`${Apiurl}/medicine_master`, {
        ...addData,
        created_by: localStorage.getItem("user_id") || "system",
      });
      setSuccessMessage("Medicine added successfully.");
      fetchData();
      closeAddModal();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error(
        "Error adding medicine:",
        error.message,
        error.response?.data
      );
      setError(
        error.response?.data?.message ||
          "Failed to add medicine. Please try again."
      );
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit input changes
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Edit form input changed: ${name} = ${value}`);
    setUpdateData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Medicine Master page");
      setError("You do not have permission to update medicine records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!editingId) {
      console.warn("Invalid medicine record ID:", editingId);
      setError("Invalid medicine record ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    setLoading(true);
    try {
      console.log("Submitting update medicine for ID:", editingId, updateData);
      await axios.put(`${Apiurl}/medicine_master/${editingId}`, {
        ...updateData,
        updated_by: localStorage.getItem("user_id") || "system",
        updated_date: new Date().toISOString(),
      });
      setSuccessMessage("Medicine updated successfully.");
      fetchData();
      closeEditModal();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error(
        "Error updating medicine:",
        error.message,
        error.response?.data
      );
      setError(
        error.response?.data?.message ||
          "Failed to update medicine. Please try again."
      );
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Reset add form
  const resetAddForm = () => {
    console.log("Resetting add form");
    setAddData({
      medicine_name: "",
      medicine_type: "",
      hsn_code: "",
      category: "",
      manufacturer: "",
      notes: "",
    });
  };

  // Edit medicine
  const handleEdit = (id, medicine) => {
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Medicine Master page");
      setError("You do not have permission to update medicine records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!id) {
      console.warn("Invalid medicine record ID:", id);
      setError("Invalid medicine record ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening edit modal for ID:", id, medicine);
    setEditingId(id);
    setUpdateData({
      medicine_name: medicine.medicine_name || "",
      medicine_type: medicine.medicine_type || "",
      hsn_code: medicine.hsn_code || "",
      category: medicine.category || "",
      manufacturer: medicine.manufacturer || "",
      notes: medicine.notes || "",
    });
    setShowEditModal(true);
  };

  // Open delete confirmation modal
  const openDeleteModal = (id) => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Medicine Master page");
      setError("You do not have permission to delete medicine records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!id) {
      console.warn("Invalid medicine record ID:", id);
      setError("Invalid medicine record ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening delete modal for ID:", id);
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    console.log("Closing delete modal");
    setShowDeleteModal(false);
    setDeleteId(null);
    setError(null);
  };

  // Handle delete medicine
  const confirmDelete = async () => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Medicine Master page");
      setError("You do not have permission to delete medicine records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!deleteId) {
      console.warn("Invalid medicine record ID:", deleteId);
      setError("Invalid medicine record ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoadingDelete(true);
    try {
      console.log("Deleting medicine with ID:", deleteId);
      await axios.delete(`${Apiurl}/medicine_master/${deleteId}`);
      setSuccessMessage("Medicine deleted successfully.");
      fetchData();
      closeDeleteModal();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error(
        "Error deleting medicine:",
        error.message,
        error.response?.data
      );
      setError(
        error.response?.data?.message ||
          "Failed to delete medicine. Please try again."
      );
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingDelete(false);
    }
  };

  // Modal handlers
  const openAddModal = () => {
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Medicine Master page");
      setError("You do not have permission to add medicine records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening add modal");
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    console.log("Closing add modal");
    setShowAddModal(false);
    resetAddForm();
    setError(null);
  };

  const closeEditModal = () => {
    console.log("Closing edit modal");
    setShowEditModal(false);
    setEditingId(null);
    setError(null);
  };

  if (loadingPrivileges) {
    return <div className="p-3 text-center">Loading permissions...</div>;
  }

  if (!hasPermission("access")) {
    console.log(
      "Access denied for role_id:",
      localStorage.getItem("role_id"),
      "on Medicine Master page"
    );
    return (
      <div className="p-3">
        <div className="alert alert-danger">
          You don&apos;t have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3 px-2">
      <style>{`
        .btn-custom {
          background-color: #01AEBE;
          color: white;
          border: none;
        }
        .btn-custom:hover {
          background-color: #018A97;
          color: white;
        }
      `}</style>

      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show mt-2">
          {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage(null)}
          ></button>
        </div>
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible fade show mt-2">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {hasPermission("create") && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <h6 className="mb-0 me-2">File Upload:</h6>
            <form ref={fileFormRef}>
              <input
                type="file"
                className="form-control form-control-sm m-2"
                accept=".csv,.xlsx"
                onChange={handleFileUpload}
                disabled={loadingUpload}
              />
            </form>
            {loadingUpload && <span className="ms-2">Uploading...</span>}
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-custom btn-sm"
              onClick={handleSampleDownload}
            >
              <Download size={16} className="me-2" />
              Sample Upload File
            </button>
            <button className="btn btn-custom btn-sm" onClick={openAddModal}>
              <i className="fas fa-plus me-2"></i> Add Medicine
            </button>
          </div>
        </div>
      )}

      {hasPermission("read") && (
        <div className="card card-body mt-2">
          <div className="row mb-2 crm-table-toolbar">
            <div className="col-md-2">
              <div className="input-group">
                <span className="input-group-text">
                  <Table2 size={16} />
                </span>
                <select
                  className="form-select"
                  value={showEntries}
                  onChange={(e) => {
                    console.log(
                      "Changing entries per page to:",
                      e.target.value
                    );
                    setShowEntries(e.target.value);
                  }}
                >
                  <option value="All">Show All</option>
                  {[5, 10, 20, 50, 100, 200, 500, 1000].map((n) => (
                    <option key={n} value={n}>
                      Show {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-md-7"></div>
            <div className="col-md-3">
              <div className="d-flex align-items-center">
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control pe-5"
                    placeholder="Search by medicine, manufacturer, type"
                    value={searchTerm}
                    onChange={(e) => {
                      console.log("Search term changed to:", e.target.value);
                      setSearchTerm(e.target.value);
                    }}
                    style={{ width: "250px", paddingRight: "2.5rem" }}
                  />
                  {searchTerm && (
                    <button
                      className="position-absolute bg-transparent border-0"
                      style={{
                        right: "30px",
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                      onClick={() => setSearchTerm("")}
                    >
                      <i className="fas fa-times text-muted"></i>
                    </button>
                  )}
                  <Search
                    size={16}
                    className="position-absolute text-muted"
                    style={{
                      right: "10px",
                      top: "35%",
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {loading && <div className="text-center py-3">Loading...</div>}

          <div className="table-responsive mt-3">
            <table className="table table-hover">
              <thead style={{ backgroundColor: "#01AEBE", color: "white" }}>
                <tr>
                  <th>S.No</th>
                  <th>Medicine</th>
                  <th>Manufacturer</th>
                  <th>Category</th>
                  <th>Medicine Type</th>
                  <th>HSN Code</th>
                  {(hasPermission("view") ||
                    hasPermission("update") ||
                    hasPermission("delete")) && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item, index) => (
                    <tr key={item._id || index} className="align-middle">
                      <td>{startIndex + index + 1}</td>
                      <td>{item.medicine_name}</td>
                      <td>{item.manufacturer || "-"}</td>
                      <td>{item.category || "-"}</td>
                      <td>{item.medicine_type || "-"}</td>
                      <td>{item.hsn_code || "-"}</td>
                      {(hasPermission("view") ||
                        hasPermission("update") ||
                        hasPermission("delete")) && (
                        <td>
                          {hasPermission("update") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              title="Edit"
                              onClick={() => handleEdit(item._id, item)}
                              disabled={!item._id}
                            >
                              <Edit size={16} className="text-primary" />
                            </button>
                          )}
                          {hasPermission("delete") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              title="Delete"
                              onClick={() => openDeleteModal(item._id)}
                              disabled={!item._id}
                            >
                              <Trash2 size={16} className="text-danger" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={
                        hasPermission("view") ||
                        hasPermission("update") ||
                        hasPermission("delete")
                          ? 6
                          : 5
                      }
                      className="text-center py-4"
                    >
                      {loading ? (
                        "Loading..."
                      ) : (
                        <div className="text-muted">
                          <i className="fas fa-search fa-2x mb-2"></i>
                          <br />
                          No medicine records found matching your search
                          criteria
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredData.length > 0 && showEntries !== "All" && (
            <div className="container">
              <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
                <button
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  onClick={() =>
                    activePage > 1 && handlePageClick(activePage - 1)
                  }
                  disabled={activePage === 1}
                >
                  <ChevronLeft size={16} />
                  <span className="d-none d-sm-inline">Previous</span>
                </button>

                <div
                  className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                  style={{ maxWidth: "100%" }}
                >
                  {getPageList().map((page, index) => (
                    <button
                      key={index}
                      className={`btn btn-sm px-3 flex-shrink-0 ${
                        page === activePage
                          ? "text-white"
                          : "btn-outline-secondary"
                      }`}
                      style={
                        page === activePage
                          ? { backgroundColor: "#01AEBE", border: "none" }
                          : {}
                      }
                      onClick={() => handlePageClick(page)}
                      disabled={page === "..."}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  onClick={() =>
                    activePage < totalPages && handlePageClick(activePage + 1)
                  }
                  disabled={activePage === totalPages}
                >
                  <span className="d-none d-sm-inline">Next</span>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Medicine Modal */}
      {hasPermission("create") && (
        <div
          className={`modal fade ${showAddModal ? "show" : ""}`}
          style={{ display: showAddModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="addMedicineModalLabel"
          aria-hidden={!showAddModal}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <h6
                  className="modal-title text-dark"
                  id="addMedicineModalLabel"
                >
                  Add Medicine
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeAddModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleAddSubmit}>
                  <div className="row">
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="category" className="form-label">
                          Category
                        </label>
                        <select
                          className="form-select"
                          id="category"
                          name="category"
                          value={addData.category}
                          onChange={handleAddInputChange}
                          required
                        >
                          <option value="">Select Category</option>
                          <option value="Yoga">Yoga</option>
                          <option value="Homeopathy">Homeopathy</option>
                          <option value="General">General</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="hsn_code" className="form-label">
                          HSN Code
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="hsn_code"
                          name="hsn_code"
                          placeholder="Enter HSN Code"
                          value={addData.hsn_code}
                          onChange={handleAddInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="medicine_name" className="form-label">
                          Medicine
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="medicine_name"
                          name="medicine_name"
                          placeholder="Enter Medicine Name"
                          value={addData.medicine_name}
                          onChange={handleAddInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="medicine_type" className="form-label">
                          Medicine Type
                        </label>
                        <select
                          className="form-control"
                          id="medicine_type"
                          name="medicine_type"
                          value={addData.medicine_type}
                          onChange={handleAddInputChange}
                          required
                        >
                          <option value="">Select Type</option>
                          <option value="Tablet">Tablet</option>
                          <option value="Capsule">Capsule</option>
                          <option value="Syrup">Syrup</option>
                          <option value="Injection">Injection</option>
                          <option value="Powder">Powder</option>
                          <option value="Sachet">Sachet</option>
                          <option value="Cream">Cream</option>
                          <option value="Ointment">Ointment</option>
                          <option value="Gel">Gel</option>
                          <option value="Drops">Drops</option>
                          <option value="Inhaler">Inhaler</option>
                          <option value="Suppository">Suppository</option>
                          <option value="Suspension">Suspension</option>
                          <option value="Spray">Spray</option>
                          <option value="Lotion">Lotion</option>
                          <option value="Solution">Solution</option>
                          <option value="Patch">Patch</option>
                          <option value="Dispersible Tablet">
                            Dispersible Tablet
                          </option>
                          <option value="Chewable Tablet">
                            Chewable Tablet
                          </option>
                          <option value="IV Fluid">IV Fluid</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="manufacturer" className="form-label">
                          Manufacturer
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="manufacturer"
                          name="manufacturer"
                          placeholder="Enter Manufacturer"
                          value={addData.manufacturer}
                          onChange={handleAddInputChange}
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="notes" className="form-label">
                          Notes
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="notes"
                          name="notes"
                          placeholder="Enter Notes"
                          value={addData.notes}
                          onChange={handleAddInputChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-4">
                    <button
                      type="submit"
                      className="btn btn-custom btn-sm px-4"
                      disabled={loading}
                    >
                      {loading ? "Adding..." : "Submit"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Medicine Modal */}
      {hasPermission("update") && (
        <div
          className={`modal fade ${showEditModal ? "show" : ""}`}
          style={{ display: showEditModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="editMedicineModalLabel"
          aria-hidden={!showEditModal}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <h6
                  className="modal-title text-dark"
                  id="editMedicineModalLabel"
                >
                  Edit Medicine
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeEditModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleEditSubmit}>
                  <div className="row">
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="category" className="form-label">
                          Category
                        </label>
                        <select
                          className="form-select"
                          id="category"
                          name="category"
                          value={updateData.category}
                          onChange={handleEditInputChange}
                          required
                        >
                          <option value="">Select Category</option>
                          <option value="Yoga">Yoga</option>
                          <option value="Homeopathy">Homeopathy</option>
                          <option value="General">General</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="hsn_code" className="form-label">
                          HSN Code
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="hsn_code"
                          name="hsn_code"
                          placeholder="Enter HSN Code"
                          value={updateData.hsn_code}
                          onChange={handleEditInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="medicine_name" className="form-label">
                          Medicine
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="medicine_name"
                          name="medicine_name"
                          placeholder="Enter Medicine Name"
                          value={updateData.medicine_name}
                          onChange={handleEditInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="medicine_type" className="form-label">
                          Medicine Type
                        </label>
                        <select
                          className="form-control"
                          id="medicine_type"
                          name="medicine_type"
                          value={updateData.medicine_type}
                          onChange={handleEditInputChange}
                          required
                        >
                          <option value="">Select Type</option>
                          <option value="Tablet">Tablet</option>
                          <option value="Capsule">Capsule</option>
                          <option value="Syrup">Syrup</option>
                          <option value="Injection">Injection</option>
                          <option value="Powder">Powder</option>
                          <option value="Sachet">Sachet</option>
                          <option value="Cream">Cream</option>
                          <option value="Ointment">Ointment</option>
                          <option value="Gel">Gel</option>
                          <option value="Drops">Drops</option>
                          <option value="Inhaler">Inhaler</option>
                          <option value="Suppository">Suppository</option>
                          <option value="Suspension">Suspension</option>
                          <option value="Spray">Spray</option>
                          <option value="Lotion">Lotion</option>
                          <option value="Solution">Solution</option>
                          <option value="Patch">Patch</option>
                          <option value="Dispersible Tablet">
                            Dispersible Tablet
                          </option>
                          <option value="Chewable Tablet">
                            Chewable Tablet
                          </option>
                          <option value="IV Fluid">IV Fluid</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="manufacturer" className="form-label">
                          Manufacturer
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="manufacturer"
                          name="manufacturer"
                          placeholder="Enter Manufacturer"
                          value={updateData.manufacturer}
                          onChange={handleEditInputChange}
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="notes" className="form-label">
                          Notes
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="notes"
                          name="notes"
                          placeholder="Enter Notes"
                          value={updateData.notes}
                          onChange={handleEditInputChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-4">
                    <button
                      type="submit"
                      className="btn btn-custom btn-sm px-4"
                      disabled={loading}
                    >
                      {loading ? "Updating..." : "Update"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {hasPermission("delete") && (
        <div
          className={`modal fade ${showDeleteModal ? "show" : ""}`}
          style={{ display: showDeleteModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="deleteMedicineModalLabel"
          aria-hidden={!showDeleteModal}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeDeleteModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body text-center">
                <div style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "40px",
                      height: "40px",
                      backgroundColor: "#fee2e2",
                      borderRadius: "50%",
                      textAlign: "center",
                      lineHeight: "40px",
                    }}
                  >
                    <AlertTriangle size={20} style={{ color: "#dc2626" }} />
                  </span>
                </div>
                <h5 className="modal-title" id="deleteMedicineModalLabel">
                  Delete Medicine
                </h5>
                <p style={{ marginTop: "10px" }}>
                  Are you sure you want to delete this medicine record?
                </p>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                  }}
                  onClick={closeDeleteModal}
                >
                  No, Keep it
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={confirmDelete}
                  disabled={loadingDelete}
                >
                  {loadingDelete ? "Deleting..." : "Yes, Delete It!"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showAddModal || showEditModal || showDeleteModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
}

export default MedicineMaster;
